import { useCallback, useEffect, useState } from "react";
import { ScorioSocket } from "@/api/ws";
import type { PublicSummary } from "./types";

export const PUBLIC_API_BASE = import.meta.env.VITE_API_URL ?? "";
export const publicApiUrl = (path: string) => `${PUBLIC_API_BASE}${path}`;
export type PublicConnection = "connecting" | "live" | "polling" | "offline";
export interface PublicResource<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  online: boolean;
  updatedAt: number | null;
  lastMessageAt: number | null;
  serverOffsetMs: number;
  connection: PublicConnection;
  refresh: () => void;
}
// One event socket refreshes the summary and all mounted heat/standings reads.
const invalidations = new EventTarget();

/** No Firebase/auth dependency: sockets invalidate the same ETag-cached GETs. */
export function usePublicResource<T>(
  path: string | null,
  socketPath?: string,
  { pollIntervalMs = 5000 }: { pollIntervalMs?: number } = {},
): PublicResource<T> {
  const initiallyOnline =
    typeof navigator === "undefined" || typeof navigator.onLine !== "boolean"
      ? true
      : navigator.onLine;
  const [snapshot, setSnapshot] = useState<
    Omit<PublicResource<T>, "refresh"> & { source: string | null }
  >({
    source: path,
    data: null,
    loading: Boolean(path),
    error: null,
    online: initiallyOnline,
    updatedAt: null,
    lastMessageAt: null,
    serverOffsetMs: 0,
    connection: initiallyOnline ? "connecting" : "offline",
  });
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  useEffect(() => {
    if (!path) return;
    let active = true;
    let etag: string | null = null;
    let inFlight = false;
    let invalidate: ReturnType<typeof setTimeout> | undefined;
    const abort = new AbortController();
    setSnapshot((value) =>
      value.source === path
        ? {
            ...value,
            loading: value.data === null && navigator.onLine,
            error: null,
            online: navigator.onLine,
            connection:
              navigator.onLine && socketPath
                ? "connecting"
                : navigator.onLine
                  ? "polling"
                  : "offline",
          }
        : {
            source: path,
            data: null,
            loading: navigator.onLine,
            error: null,
            online: navigator.onLine,
            updatedAt: null,
            lastMessageAt: null,
            serverOffsetMs: 0,
            connection:
              navigator.onLine && socketPath
                ? "connecting"
                : navigator.onLine
                  ? "polling"
                  : "offline",
          },
    );
    const load = async () => {
      if (!active || inFlight || !navigator.onLine) return;
      inFlight = true;
      try {
        const sentAt = Date.now();
        const response = await fetch(publicApiUrl(path), {
          credentials: "omit",
          signal: abort.signal,
          headers: etag ? { "If-None-Match": etag } : {},
        });
        if (!active) return;
        const serverDate = response.headers.get("Date");
        if (serverDate && Number.isFinite(Date.parse(serverDate))) {
          const age = Math.max(0, Number(response.headers.get("Age")) || 0);
          const serverOffsetMs = Date.parse(serverDate) + age * 1000 - (sentAt + Date.now()) / 2;
          setSnapshot((value) => ({ ...value, serverOffsetMs }));
        }
        if (response.status === 304) {
          setSnapshot((value) => ({
            ...value,
            loading: false,
            error: null,
            updatedAt: Date.now(),
          }));
          return;
        }
        if (response.status === 404 || response.status === 403) {
          etag = null;
          setSnapshot((value) => ({
            ...value,
            data: null,
            loading: false,
            error: "This page is unavailable.",
          }));
          return;
        }
        if (!response.ok)
          throw new Error(
            response.status === 404
              ? "This page is unavailable."
              : "Check your connection and try again.",
          );
        const data = (await response.json()) as T;
        if (!active) return;
        etag = response.headers.get("etag");
        setSnapshot((value) => ({
          ...value,
          data,
          loading: false,
          error: null,
          updatedAt: Date.now(),
        }));
      } catch (error) {
        if (active && !abort.signal.aborted)
          setSnapshot((value) => ({
            ...value,
            loading: false,
            error: error instanceof Error ? error.message : "Could not load this page.",
            connection: navigator.onLine ? "polling" : "offline",
          }));
      } finally {
        inFlight = false;
      }
    };
    const queueLoad = () => {
      clearTimeout(invalidate);
      invalidate = setTimeout(() => void load(), 100);
    };
    const sharedMessage = (event: Event) => {
      const base = (event as CustomEvent<string>).detail;
      if (path.startsWith(`${base}/`)) {
        setSnapshot((value) => ({ ...value, lastMessageAt: Date.now() }));
        queueLoad();
      }
    };
    let socket: ScorioSocket | null = null;
    if (socketPath) {
      const url = new URL(publicApiUrl(socketPath), window.location.href);
      url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
      socket = new ScorioSocket(url.toString());
      socket.onStatus((connected) => {
        if (active)
          setSnapshot((value) => ({
            ...value,
            connection: connected ? "live" : navigator.onLine ? "polling" : "offline",
          }));
      });
      socket.subscribe((message) => {
        if (!active) return;
        setSnapshot((value) => ({ ...value, lastMessageAt: Date.now() }));
        const kind: string = message.type;
        if (kind === "poll_fallback" || kind === "poll") {
          socket?.disconnect();
          setSnapshot((value) => ({ ...value, connection: "polling" }));
        }
        invalidations.dispatchEvent(
          new CustomEvent("message", { detail: socketPath.replace(/\/ws$/, "") }),
        );
        queueLoad();
      });
      if (navigator.onLine) socket.connect();
    }
    const online = () => {
      setSnapshot((value) => ({
        ...value,
        online: navigator.onLine,
        connection: navigator.onLine ? "connecting" : "offline",
      }));
      if (navigator.onLine) {
        void load();
        socket?.connect();
      } else socket?.disconnect();
    };
    void load();
    const polling = setInterval(() => void load(), pollIntervalMs);
    invalidations.addEventListener("message", sharedMessage);
    window.addEventListener("online", online);
    window.addEventListener("offline", online);
    return () => {
      active = false;
      abort.abort();
      socket?.disconnect();
      clearInterval(polling);
      clearTimeout(invalidate);
      invalidations.removeEventListener("message", sharedMessage);
      window.removeEventListener("online", online);
      window.removeEventListener("offline", online);
    };
  }, [path, socketPath, revision, pollIntervalMs]);
  return snapshot.source === path
    ? { ...snapshot, refresh }
    : {
        ...snapshot,
        data: null,
        loading: Boolean(path),
        error: null,
        refresh,
      };
}
export function publicEventPath(slug: string, surface: "results" | "tv" = "results") {
  return `/api/public/${encodeURIComponent(slug)}${surface === "tv" ? "/tv" : ""}`;
}
export function usePublicEvent(
  slug: string,
  {
    surface = "results",
    pollIntervalMs,
  }: { surface?: "results" | "tv"; pollIntervalMs?: number } = {},
) {
  const path = publicEventPath(slug, surface);
  const state = usePublicResource<PublicSummary>(path, `${path}/ws`, {
    pollIntervalMs: pollIntervalMs ?? (surface === "tv" ? 60000 : 5000),
  });
  const [preciseClock, setPreciseClock] = useState<{ path: string; offset: number } | null>(null);
  const timerEnabled = Boolean(state.data?.published && state.data.modules.timer);
  const connected = state.connection === "live";
  useEffect(() => {
    if (!timerEnabled || !state.online) return;
    let active = true;
    const abort = new AbortController();
    const sample = async () => {
      const sent = Date.now();
      try {
        const response = await fetch(publicApiUrl(`${path}/time`), {
          credentials: "omit",
          cache: "no-store",
          signal: abort.signal,
        });
        if (!response.ok) return;
        const value = (await response.json()) as { server_time?: unknown };
        if (
          active &&
          typeof value.server_time === "string" &&
          Number.isFinite(Date.parse(value.server_time))
        )
          setPreciseClock({
            path,
            offset: Date.parse(value.server_time) - (sent + Date.now()) / 2,
          });
      } catch {
        /* The public Date/Age sample remains a safe fallback. */
      }
    };
    void sample();
    const interval = setInterval(() => void sample(), 60000);
    return () => {
      active = false;
      abort.abort();
      clearInterval(interval);
    };
  }, [path, timerEnabled, state.online, connected]);
  return {
    ...state,
    serverOffsetMs: preciseClock?.path === path ? preciseClock.offset : state.serverOffsetMs,
  };
}
